/**
 * AddPropertyInlineForm
 *
 * Compact single-row form for adding a property to an existing custom field.
 * Appears at the bottom of a custom field accordion when the user clicks
 * "+ Add Property". Collapses back to a button after submission.
 */

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { clampPageInput } from "@/utils/fields";
import { PlusIcon, XIcon } from "lucide-react";
import { useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AddPropertyInlineFormProps {
    /** ID of the custom field to add to. Omit for extracted field bridge-or-add flows. */
    customFieldId?: number;
    isEditable: boolean;
    /** Current visible PDF page (1-indexed), used as default page number */
    currentPage?: number;
    /** Total pages in the PDF, used for validation max */
    totalPages?: number;
    isPending: boolean;
    onSubmit: (data: {
        customFieldId: number;
        name: string;
        value: string | null;
        pageNumber: number | null;
    }) => void;
}

interface FormState {
    name: string;
    value: string;
    page: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AddPropertyInlineForm({
    customFieldId,
    isEditable,
    currentPage,
    totalPages,
    isPending,
    onSubmit,
}: AddPropertyInlineFormProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [form, setForm] = useState<FormState>({
        name: "",
        value: "",
        page: currentPage ? String(currentPage) : "",
    });
    const [nameError, setNameError] = useState(false);
    const [valueError, setValueError] = useState(false);
    const nameRef = useRef<HTMLInputElement>(null);
    const valueRef = useRef<HTMLInputElement>(null);

    if (!isEditable) return null;

    function reset() {
        setForm({
            name: "",
            value: "",
            page: currentPage ? String(currentPage) : "",
        });
        setNameError(false);
        setValueError(false);
        setIsOpen(false);
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!form.name.trim()) {
            setNameError(true);
            nameRef.current?.focus();
            return;
        }
        if (!form.value.trim()) {
            setValueError(true);
            valueRef.current?.focus();
            return;
        }

        const pageNum = form.page.trim() ? parseInt(form.page, 10) : null;
        if (pageNum !== null && (isNaN(pageNum) || pageNum < 1)) return;
        if (pageNum !== null && totalPages && pageNum > totalPages) return;

        onSubmit({
            customFieldId: customFieldId ?? 0,
            name: form.name.trim(),
            value: form.value.trim(),
            pageNumber: pageNum,
        });
        reset();
    }

    // ── Collapsed: just a trigger button ──
    if (!isOpen) {
        return (
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 px-1 py-0.5 text-xs transition-colors"
            >
                <PlusIcon size={12} />
                Add Property
            </button>
        );
    }

    // ── Expanded: inline form row ──
    return (
        <form
            onSubmit={handleSubmit}
            className="flex items-center gap-1.5 px-1 py-1"
        >
            <Input
                ref={nameRef}
                placeholder="Property name *"
                value={form.name}
                onChange={(e) => {
                    setForm((p) => ({ ...p, name: e.target.value }));
                    if (nameError) setNameError(false);
                }}
                disabled={isPending}
                aria-invalid={nameError}
                className={`h-7 min-w-0 flex-[2] text-xs ${nameError ? "border-destructive" : ""}`}
                autoFocus
                autoComplete="off"
            />
            <Input
                ref={valueRef}
                placeholder="Value *"
                value={form.value}
                onChange={(e) => {
                    setForm((p) => ({ ...p, value: e.target.value }));
                    if (valueError) setValueError(false);
                }}
                disabled={isPending}
                aria-invalid={valueError}
                className={`h-7 min-w-0 flex-[2] text-xs ${valueError ? "border-destructive" : ""}`}
                autoComplete="off"
            />
            <Input
                type="number"
                placeholder={currentPage ? String(currentPage) : "Pg"}
                min={1}
                max={totalPages}
                value={form.page}
                onChange={(e) => {
                    const clamped = clampPageInput(e.target.value, totalPages);
                    if (clamped !== null)
                        setForm((p) => ({ ...p, page: clamped }));
                }}
                disabled={isPending}
                className="h-7 w-14 shrink-0 [appearance:textfield] text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                autoComplete="off"
            />
            <Button
                type="submit"
                variant="default"
                size="icon-xs"
                disabled={isPending}
                className="h-7 w-7 shrink-0"
                aria-label="Add property"
            >
                {isPending ? (
                    <Spinner className="h-3 w-3" />
                ) : (
                    <PlusIcon size={14} />
                )}
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={reset}
                disabled={isPending}
                className="h-7 w-7 shrink-0"
                aria-label="Cancel"
            >
                <XIcon size={14} />
            </Button>
        </form>
    );
}
